import { InputWithLabel } from '@adotel/components'
import { Button, Grid, IconButton } from '@material-ui/core'
import { CircularProgress } from '@material-ui/core'
import Switch from '@material-ui/core/Switch'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import React, { useState } from 'react'

import { getTranslate } from '../../localization'
import {
  NotificationSettings,
  ProviderConnectionsItemBoolean,
  ProviderSubmit
} from '../../types/accommodationProviderSubmit'
import { bookingNotesEnabledForAdapter } from './BookingNotesSwitch'
import ProviderARI from './ProviderARI'
import ProviderAddons from './ProviderAddons'
import ProviderAssociatedGateways from './ProviderAssociatedGateways'
import ProviderConnections from './ProviderConnections'
import useStyles from './Styles/ProviderSubmitBox.style'

interface ProviderSubmitBoxProps {
  selectedProviderSubmit: ProviderSubmit[]
  setSelectedProviderSubmit: React.Dispatch<
    React.SetStateAction<ProviderSubmit[]>
  >
  selectedProviderSubmitIndex: number
  providersSubmitGenerate: (v: any) => void
  propertyCode: string
  existProviderCodes: string[]
  loadingForMutation: boolean
  providersSubmitModify: (v: any) => void
}

export default function ProviderSubmitBox({
  selectedProviderSubmit,
  setSelectedProviderSubmit,
  selectedProviderSubmitIndex,
  providersSubmitGenerate,
  propertyCode,
  existProviderCodes,
  loadingForMutation,
  providersSubmitModify
}: ProviderSubmitBoxProps) {
  const classes = useStyles()
  const [clickOnBox, setClickOnBox] = useState(false)

  const [valuesBlur, setValuesBlur] = useState<ProviderConnectionsItemBoolean>({
    providerURLBooking: false,
    providerURLRates: false,
    providerURLRestriction: false,
    providerURLAvailability: false
  })
  const [clickedProviderIndex, setClickedProviderIndex] = useState<number>(-1)

  const regexUrl = (url: string) => {
    const UrlPattern = /^(https?|ftp):\/\/.+$/
    return UrlPattern.test(url)
  }

  const isProviderExist = existProviderCodes.includes(
    selectedProviderSubmit[selectedProviderSubmitIndex].providerCode
  )

  const onSave = (selectedIndex: number) => {
    setClickedProviderIndex(selectedIndex)

    // 🔥 Deep clone to unfreeze
    const clonedSelectedProviderSubmit: ProviderSubmit[] = JSON.parse(
      JSON.stringify(selectedProviderSubmit)
    )

    clonedSelectedProviderSubmit[selectedProviderSubmitIndex].addOns.forEach(
      (ele) => {
        delete ele.notDisabled
      }
    )

    const {
      providerName,
      providerNameTranslatedList,
      ARIRetrievals,
      displayProviderCode,
      ...updatedProviderSubmit
    } = clonedSelectedProviderSubmit[selectedProviderSubmitIndex]

    // Persist only the opt-in the UI can display as active. Legacy/missing
    // capabilities fail closed; the server independently verifies eligibility.
    updatedProviderSubmit.providerConnections.booking.bookingNotesEnabled =
      bookingNotesEnabledForAdapter(
        updatedProviderSubmit.providerConnections.booking,
        updatedProviderSubmit.providerConnections.booking.providerAdapter
      )
    // The server's capability catalog is output-only, never mutation input.
    delete updatedProviderSubmit.providerConnections.booking
      .bookingNotesSupportedAdapters

    updatedProviderSubmit.providerConnections.availability.retrievalInterval =
      ARIRetrievals.find(
        (ele) =>
          ele.adapter ===
          updatedProviderSubmit.providerConnections.availability.providerAdapter
      )?.retrievalInterval

    updatedProviderSubmit.providerConnections.rate.retrievalInterval =
      ARIRetrievals.find(
        (ele) =>
          ele.adapter ===
          updatedProviderSubmit.providerConnections.rate.providerAdapter
      )?.retrievalInterval

    updatedProviderSubmit.providerConnections.restriction.retrievalInterval =
      ARIRetrievals.find(
        (ele) =>
          ele.adapter ===
          updatedProviderSubmit.providerConnections.restriction.providerAdapter
      )?.retrievalInterval

    let tempNotification: NotificationSettings[] = []
    if (
      clonedSelectedProviderSubmit[selectedProviderSubmitIndex]
        .notificationSettings
    ) {
      tempNotification = clonedSelectedProviderSubmit[
        selectedProviderSubmitIndex
      ]
        .notificationSettings!.map((ele) => {
          const filteredChannelDeliveries = ele.channelDeliveries.filter(
            (item) => item.active
          )

          if (filteredChannelDeliveries.length > 0) {
            return {
              ...ele,
              channelDeliveries: filteredChannelDeliveries
            }
          }

          return null
        })
        .filter(Boolean) as NotificationSettings[]
    }

    if (isProviderExist) {
      providersSubmitModify({
        variables: {
          propertyCode: propertyCode,
          input: {
            ...updatedProviderSubmit,
            notificationSettings: tempNotification
          }
        }
      })
    } else {
      providersSubmitGenerate({
        variables: {
          propertyCode: propertyCode,
          input: {
            ...updatedProviderSubmit,
            notificationSettings: tempNotification
          }
        }
      })
    }
  }

  const isDisabledSaveButton =
    !selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
      .availability.providerAdapter ||
    (selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
      .availability.providerURL &&
      !regexUrl(
        selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
          .availability.providerURL
      )) ||
    !selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
      .rate.providerAdapter ||
    (selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
      .rate.providerURL &&
      !regexUrl(
        selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
          .rate.providerURL
      )) ||
    !selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
      .restriction.providerAdapter ||
    (selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
      .restriction.providerURL &&
      !regexUrl(
        selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
          .restriction.providerURL
      )) ||
    !selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
      .booking.providerAdapter ||
    (selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
      .booking.providerURL &&
      !regexUrl(
        selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
          .booking.providerURL
      )) ||
    !selectedProviderSubmit[selectedProviderSubmitIndex].providerConnections
      .booking.bookingTimeout ||
    selectedProviderSubmit[selectedProviderSubmitIndex].addOns.some(
      (a) => !a.code
    ) ||
    selectedProviderSubmit[selectedProviderSubmitIndex].addOns.some(
      (a) => !a.name
    ) ||
    selectedProviderSubmit[selectedProviderSubmitIndex].addOns.some(
      (a) => !a.nameTranslatedList[0].value
    )

  return (
    <div className={classes.root}>
      <div
        className={[
          classes.inputs,
          clickOnBox && classes.marginBottomInputs
        ].join(' ')}>
        <div className={classes.inputsContainer}>
          <div className={classes.inputSection}>
            <div className={classes.inputProviderName}>
              <InputWithLabel
                disabled={true}
                label={getTranslate('Provider Name')}
                placeholder={getTranslate('Enter the Provider Name')}
                autoComplete="off"
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerName
                }
                inputSize="small"
                fullWidth
              />
              <InputWithLabel
                className={classes.fontFamilyInput}
                disabled={true}
                label={getTranslate('Provider Persian Name')}
                placeholder={getTranslate('Enter the Provider Persian Name')}
                autoComplete="off"
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .providerNameTranslatedList
                    ? selectedProviderSubmit[selectedProviderSubmitIndex]
                        .providerNameTranslatedList![0].value
                    : ''
                }
                inputSize="small"
                fullWidth
              />
              <InputWithLabel
                disabled={true}
                label={getTranslate('Provider Code')}
                placeholder={getTranslate('Enter the Provider Code')}
                autoComplete="off"
                value={
                  selectedProviderSubmit[selectedProviderSubmitIndex]
                    .displayProviderCode
                }
                inputSize="small"
                fullWidth
              />
            </div>
          </div>
          <div className={classes.switchAndButton}>
            <Grid
              component="label"
              container
              alignItems="center"
              justify="flex-start"
              className={classes.switchForProviderSubmit}>
              <Grid item>{getTranslate('InActive')}</Grid>
              <Grid item>
                <Switch
                  name="checkProviderSubmit"
                  color="primary"
                  onChange={(e) =>
                    setSelectedProviderSubmit([
                      ...selectedProviderSubmit.slice(
                        0,
                        selectedProviderSubmitIndex
                      ),
                      {
                        ...selectedProviderSubmit[selectedProviderSubmitIndex],
                        providerConnections: {
                          ...selectedProviderSubmit[selectedProviderSubmitIndex]
                            .providerConnections,
                          status: e.target.checked ? 'Active' : 'InActive'
                        }
                      },
                      ...selectedProviderSubmit.slice(
                        selectedProviderSubmitIndex + 1
                      )
                    ])
                  }
                  checked={
                    selectedProviderSubmit[selectedProviderSubmitIndex]
                      .providerConnections.status === 'Active'
                  }
                />
              </Grid>
              <Grid item>{getTranslate('Active')}</Grid>
            </Grid>
            <div className={classes.mainDropDownIcon}>
              <Button
                variant="contained"
                color="primary"
                size="medium"
                disabled={
                  isDisabledSaveButton ||
                  (loadingForMutation &&
                    clickedProviderIndex === selectedProviderSubmitIndex)
                }
                onClick={() => {
                  onSave(selectedProviderSubmitIndex)
                }}>
                {getTranslate('Save')}
                {loadingForMutation &&
                  clickedProviderIndex === selectedProviderSubmitIndex && (
                    <CircularProgress
                      className={classes.circularProgress}
                      size={16}
                    />
                  )}
              </Button>
              <IconButton
                onClick={() => {
                  setClickOnBox(!clickOnBox)
                }}>
                {clickOnBox ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </div>
          </div>
        </div>
      </div>
      {clickOnBox && (
        <>
          <ProviderARI
            selectedProviderSubmit={selectedProviderSubmit}
            setSelectedProviderSubmit={setSelectedProviderSubmit}
            selectedProviderSubmitIndex={selectedProviderSubmitIndex}
          />
          <ProviderConnections
            selectedProviderSubmit={selectedProviderSubmit}
            setSelectedProviderSubmit={setSelectedProviderSubmit}
            selectedProviderSubmitIndex={selectedProviderSubmitIndex}
            valuesBlur={valuesBlur}
            setValuesBlur={setValuesBlur}
            regexUrl={regexUrl}
          />
          <ProviderAssociatedGateways
            selectedProviderSubmit={selectedProviderSubmit}
            setSelectedProviderSubmit={setSelectedProviderSubmit}
            selectedProviderSubmitIndex={selectedProviderSubmitIndex}
          />
          <ProviderAddons
            selectedProviderSubmit={selectedProviderSubmit}
            setSelectedProviderSubmit={setSelectedProviderSubmit}
            selectedProviderSubmitIndex={selectedProviderSubmitIndex}
            isProviderExist={isProviderExist}
          />
        </>
      )}
    </div>
  )
}
